import React from "react";
import { useNavigate } from "react-router-dom";
import { Tooltip } from "@mui/material";
import { getDashboardSurveyGroups } from "../../../Actions/DashboardActions";
import { useQuery } from "@tanstack/react-query";
import NoData from "../../../components/NoData";

const MySurveyGroups = ({ user }) => {
  const navigate = useNavigate();
  const {
    data: surveyGroupsData,
    isLoading: surveyGroupsDataLoading,
    isError: surveyGroupsDataError,
  } = useQuery({
    queryFn: () => getDashboardSurveyGroups(user?.organizationId),
    queryKey: ["dashboardSurveyGroups", user?.organizationId],
    enabled: !!user?.organizationId,
    staleTime: 10000,
    cacheTime: 15000,
  });

  return (
    <div className="w-full bg-white border border-[#E0E0E0] rounded-xl h-[360px] flex flex-col overflow-hidden">
      <div className="px-6 py-4 border-b border-[#E0E0E0] flex items-center justify-between bg-gray-50/50">
        <div>
          <h1 className="text-[#0226BE] text-[16px] font-[500]">Survey Groups</h1>
          <p className="text-[11px] text-[#666] mt-0.5">Organize your surveys</p>
        </div>
        <button
          className="text-[#0226BE] text-[13px] font-[500] hover:bg-blue-50 px-3 py-1.5 rounded-lg transition-all cursor-pointer flex items-center gap-1"
          onClick={(e) => navigate("/survey-groups")}
        >
          See all
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
      <div className="flex-1 overflow-y-auto containerScroll p-4">
        {surveyGroupsDataLoading ? (
          <div className="w-full text-center py-6 flex items-center justify-center flex-col space-y-3 h-full">
            <div className="w-8 h-8 border-2 border-[#0226BE] border-t-transparent rounded-full animate-spin"></div>
            <p className="text-[#666] text-[13px]">Loading groups...</p>
          </div>
        ) : surveyGroupsDataError ? (
          <div className="w-full text-center py-6 flex items-center justify-center flex-col space-y-3 h-full">
            <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
              <svg className="w-6 h-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <p className="text-[#e12121] text-[13px] font-medium">Something went wrong!</p>
          </div>
        ) : surveyGroupsData?.length ? (
          <div className="space-y-3">
            {surveyGroupsData?.map((data, ind) => (
              <div
                key={ind}
                className="p-4 border border-[#E0E0E0] rounded-xl hover:border-[#0226BE]/30 hover:shadow-sm transition-all cursor-pointer group"
                onClick={(e) => navigate(`/survey-groups/${data?._id}`)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-100 to-green-50 flex items-center justify-center flex-shrink-0">
                      <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <Tooltip title={data?.groupName} placement="top">
                        <h3 className="text-[#333] font-[600] text-[14px] truncate group-hover:text-[#0226BE] transition-colors">
                          {data?.groupName}
                        </h3>
                      </Tooltip>
                      <Tooltip title={data?.description} placement="bottom">
                        <p className="text-[#666] text-[12px] mt-0.5 truncate max-w-[200px]">
                          {data?.description || "No description"}
                        </p>
                      </Tooltip>
                      <div className="flex items-center gap-2 mt-2">
                        <span className="text-[11px] text-[#666] bg-gray-100 px-2 py-0.5 rounded-md">
                          {data?.surveys?.length || 0} survey{data?.surveys?.length !== 1 ? 's' : ''}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div>
                    {data?.isActive ? (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-green-50 border border-green-200 text-green-700 text-[10px] font-[600] rounded-full">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>
                        Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-gray-50 border border-gray-200 text-gray-600 text-[10px] font-[600] rounded-full">
                        <span className="w-1.5 h-1.5 rounded-full bg-gray-400"></span>
                        Inactive
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="h-full flex items-center justify-center">
            <NoData
              msg={
                <button
                  className="text-[#0226BE] font-[500] hover:underline cursor-pointer"
                  onClick={(e) => navigate("/survey-groups")}
                >
                  Create your first group
                </button>
              }
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default MySurveyGroups;
